import type RootStore from "stores/RootStore";
import { RouteType } from "types/RouteType";
import { throwIfFailed } from "shared/dialogSubmit";
import ClientDetailStore from "./ClientDetailStore";
import type ClientOverviewStore from "./ClientOverviewStore";
import { isClientsAdmin } from "../rbac";
import ClientsStore from "../ClientList/ClientsStore";
import InstanceActionsStore from "../../infrastructure/InstanceActions/InstanceActionsStore";
import type InstanceUsersStore from "../../infrastructure/InstanceUsers/InstanceUsersStore";
import { instanceUsersDialogs } from "../../infrastructure/InstanceUsers/instanceUsersDialogs";
import type ClientInquiryAnswersStore from "../ClientInquiries/ClientInquiryAnswersStore";
import whiteboardRoute from "../../whiteboard/whiteboard.Route";
import { WHITEBOARD_ROW_DIALOG } from "../../whiteboard/WhiteboardStore";
import type WhiteboardStore from "../../whiteboard/WhiteboardStore";
import type ClientTicketsStore from "../../tickets/ClientTicketsStore";
import type ContractsStore from "../Contracts/ContractsStore";
import type OnboardingStore from "../Onboarding/OnboardingStore";
import type ReceivablesStore from "../../billing/Receivables/ReceivablesStore";
import { clientNewTicketDialog } from "../../tickets/clientNewTicketDialog";
import type { DialogType } from "types/DialogType";

const instanceUsersOf = (rootStore: RootStore) =>
  rootStore.instanceUsersViewStore as unknown as InstanceUsersStore | undefined;

// The Inquiries tab's store: the Client Inquiries grid pinned to this client.
const inquiryAnswersOf = (rootStore: RootStore) =>
  rootStore.clientInquiryAnswersTabStore as unknown as ClientInquiryAnswersStore | undefined;

// The Tickets tab's store: every ticket about this client.
const clientTicketsOf = (rootStore: RootStore) =>
  rootStore.clientTicketsTabStore as unknown as ClientTicketsStore | undefined;

// Blake's trackers (#3075 / #2663): the Onboarding, Contracts and Billing tabs'
// stores, each pinned to this client.
const trackerTabsOf = (rootStore: RootStore) => {
  const root = rootStore as unknown as {
    clientOnboardingViewStore?: OnboardingStore;
    clientContractsViewStore?: ContractsStore;
    clientReceivablesViewStore?: ReceivablesStore;
  };
  return [root.clientOnboardingViewStore, root.clientContractsViewStore, root.clientReceivablesViewStore];
};

const pinTrackerTabs = (rootStore: RootStore, id: string) => {
  for (const store of trackerTabsOf(rootStore)) void store?.initialize(id);
};

// The Overview's one read (and the API's answer to "may this person change
// this client?", which every tab's write controls follow).
const clientOverviewOf = (rootStore: RootStore) =>
  rootStore.clientOverviewViewStore as unknown as ClientOverviewStore | undefined;

/** Admins may open an archived client, so their overview asks for it too. */
const loadOverview = (rootStore: RootStore, id: string) =>
  void clientOverviewOf(rootStore)?.initializeForClient(id, { includeDeleted: isClientsAdmin(rootStore) });

/**
 * The Whiteboard's lane dialog, registered here too so the Whiteboard tab's
 * Add lane opens it on Client 360 (dialogs are per-route). Its title, body,
 * size, label and validation are the board's own — taken from the board's
 * registration so the two cannot drift — and only the submit differs: a
 * refused save THROWS (the dialog stays open with the reason in its footer),
 * and a saved one re-reads this client's lanes so the tab shows it.
 */
const boardLaneDialog = whiteboardRoute[0]?.dialogs?.find(
  (d) => d.name === WHITEBOARD_ROW_DIALOG,
);

export const clientWhiteboardLaneDialog: DialogType = {
  ...boardLaneDialog,
  name: WHITEBOARD_ROW_DIALOG,
  title: boardLaneDialog?.title ?? "Whiteboard lane",
  component:
    boardLaneDialog?.component ??
    (() => import("../../whiteboard/dialogs/WhiteboardRowDialog.Body")),
  submitButtonText: boardLaneDialog?.submitButtonText ?? "Save lane",
  onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
    const board = rootStore.whiteboardStore as WhiteboardStore | undefined;
    if (!board || !(await board.submitRowDialog())) {
      throw new Error(board?.actionError || "The lane could not be saved.");
    }
    await (rootStore.clientDetailStore as ClientDetailStore | undefined)?.loadWhiteboard();
  },
  onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) =>
    (rootStore.whiteboardStore as WhiteboardStore | undefined)?.rowDraftValid === true,
};

const clientDetailRoute: RouteType[] = [
  {
    name: "ClientDetail",
    path: "/clients/:id/:tab?",
    layout: "/app",
    viewportCard: {
      header: {
        enableBackButton: true,
        title: "Client Detail",
        enableRefreshButton: true,
        component: () => import("./ClientDetail.Header"),
      },
      body: {
        component: () => import("./ClientDetail.Body"),
        grayBackground: true,
      },
    },
    // Parameterized route — hidden from the nav tree (the list route owns the
    // "Clients" nav entry). RBAC seam: reads require auth; the API enforces the
    // operator-or-admin gate on the writes triggered from the Modules/Tools tabs.
    group: "Clients",
    hidden: true,
    requiresAuth: true,
    requiresPermission: false,
    // The @intrasync/react right-nav menu rows always navigate
    // goTo(rightNavDefaultRoute, { jobNumber }) with the client code (falling
    // back to id) in jobNumber — normalize that to the :id param this route
    // actually uses, resolving codes back to ids via AppLayoutStore.
    beforeEnter: ({ rootStore, params }) => {
      if (!params.id && params.jobNumber) {
        const key = String(params.jobNumber);
        // Map a client code back to its id (the right-nav passes code-or-id as
        // jobNumber). Was AppLayoutStore.resolveRightNavClientId on the local
        // fork; the library store (26.7.17) has no HQ client knowledge.
        const clientStore = rootStore.clientStore as
          | { clients?: Array<{ id: string; code?: string | null }> }
          | undefined;
        const clients = clientStore?.clients ?? [];
        const resolved =
          clients.find((c) => c.code === key || c.id === key)?.id ?? key;
        void rootStore.routerStore?.goTo("ClientDetail", { id: resolved });
        return false;
      }
      return true;
    },
    onEnter: ({ rootStore, params }) => {
      const store = rootStore.clientDetailStore as ClientDetailStore;
      const id = params.id as string;
      if (id) {
        store.setClientId(id);
        store.loadClientDetail();
        // Users tab: this client's instance connections, and its users when a
        // key is configured (HQ's API makes that call, not the browser).
        void instanceUsersOf(rootStore)?.initialize(id);
        // Inquiries tab: this client's answers (one page, newest first).
        void inquiryAnswersOf(rootStore)?.initializeForClient(id);
        // Tickets tab: every ticket about this client.
        void clientTicketsOf(rootStore)?.initializeForClient(id);
        // Onboarding, Contracts and Billing (receivables, payments).
        pinTrackerTabs(rootStore, id);
        // Overview: one read of everything it shows.
        loadOverview(rootStore, id);
      }
    },
    onParamsChange: ({ rootStore, params }) => {
      const store = rootStore.clientDetailStore as ClientDetailStore;
      const id = params.id as string;
      if (id && store.clientId !== id) {
        store.setClientId(id);
        store.loadClientDetail();
        void instanceUsersOf(rootStore)?.initialize(id);
        void inquiryAnswersOf(rootStore)?.initializeForClient(id);
        void clientTicketsOf(rootStore)?.initializeForClient(id);
        pinTrackerTabs(rootStore, id);
        loadOverview(rootStore, id);
      }
    },
    // Tear down the live client-event subscription (shared refcounted socket —
    // see ../clientsRealtime.ts) when leaving the detail view. One-time secrets
    // (a temporary password, a new key's config) never outlive the page.
    onExit: ({ rootStore }) => {
      const store = rootStore.clientDetailStore as ClientDetailStore;
      store.disposeRealtime();
      // reset(), not just clearing secrets: it also invalidates every in-flight
      // Users-tab request, so a reply landing after the page is gone cannot
      // repopulate a password or key, or reopen a dialog somewhere else.
      instanceUsersOf(rootStore)?.reset();
      // Clean slate, so another client's answers never flash on the next visit.
      inquiryAnswersOf(rootStore)?.reset();
      clientTicketsOf(rootStore)?.reset();
      for (const store of trackerTabsOf(rootStore)) store?.reset();
      clientOverviewOf(rootStore)?.reset();
    },
    dialogs: [
      {
        // Reuses the shared ClientForm (backed by ClientsStore) so editing a
        // client from the detail hub goes through the exact same form/validation
        // path as the list. The header seeds ClientsStore from the loaded client.
        name: "ClientFormDialog",
        title: "Edit Client",
        submitButtonText: "Save client",
        component: () => import("../ClientList/dialogs/ClientForm.Body"),
        cancelButton: true,
        height: 640,
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const clientsViewStore = rootStore.clientsViewStore as ClientsStore;
          const detailStore = rootStore.clientDetailStore as ClientDetailStore;
          // A refused save throws: the dialog stays open with the reason in
          // its footer (shared/dialogSubmit).
          throwIfFailed(
            await clientsViewStore.saveClient(),
            clientsViewStore.formError,
            "The client could not be saved."
          );
          await detailStore.loadClientDetail();
          // A new parent changes the group the Overview rolls up.
          void clientOverviewOf(rootStore)?.load({ includeDeleted: isClientsAdmin(rootStore) });
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const clientsViewStore = rootStore.clientsViewStore as ClientsStore;
          return clientsViewStore.isFormValid;
        },
      },
      // ---- Instance action dialogs (InstancesTab, Phase 5) ----
      // The shared infrastructure Deploy/Rollback/Confirm dialogs, registered
      // here so the Client detail Instances tab can open them. operator+; the
      // API re-enforces. The confirm dialog runs the lifecycle action through
      // ClientDetailStore so the tab refreshes in place.
      {
        name: "InstanceDeployDialog",
        title: "Deploy Image",
        component: () =>
          import("../../infrastructure/InstanceActions/dialogs/DeployDialog.Body"),
        cancelButton: true,
        height: 420,
        submitButtonText: "Deploy",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const detail = rootStore.clientDetailStore as ClientDetailStore;
          // A refused deploy throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitDeploy(), actions.deployError, "The image could not be deployed.");
          await detail.loadInstances();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isDeployValid && actions.canManage;
        },
      },
      {
        name: "InstanceRollbackDialog",
        title: "Roll Back Image",
        component: () =>
          import(
            "../../infrastructure/InstanceActions/dialogs/RollbackDialog.Body"
          ),
        cancelButton: true,
        height: 520,
        submitButtonText: "Roll Back",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const detail = rootStore.clientDetailStore as ClientDetailStore;
          // A refused rollback throws: the dialog stays open with the reason
          // in its footer (shared/dialogSubmit).
          throwIfFailed(await actions.submitRollback(), actions.rollbackError, "The rollback could not be started.");
          await detail.loadInstances();
        },
        onSubmitValidation: ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          return actions.isRollbackValid && actions.canManage;
        },
      },
      {
        name: "InstanceConfirmActionDialog",
        title: "Confirm Action",
        component: () =>
          import(
            "../../infrastructure/InstanceActions/dialogs/ConfirmActionDialog.Body"
          ),
        cancelButton: true,
        // Tall enough for the progress panel the body shows once Confirm is tapped.
        height: 440,
        submitButtonText: "Confirm",
        onSubmit: async ({ rootStore }: { rootStore: RootStore }) => {
          const actions = rootStore.instanceActionsViewStore as InstanceActionsStore;
          const detail = rootStore.clientDetailStore as ClientDetailStore;
          // 'update-to-latest' is server-resolved (the API picks the digest),
          // so it has its own runner rather than going through the generic
          // lifecycle transport — it reaches this dialog because the audit
          // found Update firing on one tap with no confirmation at all.
          await actions.runConfirmed(async (instance, action, progressToken) => {
            if (action === "update-to-latest") {
              const ok = await actions.updateToLatest(instance);
              if (ok) await detail.loadInstances?.();
              return ok;
            }
            return detail.runInstanceLifecycle(instance, action, progressToken);
          });
        },
      },
      // ---- Users tab: users on the client's own instance ----
      ...instanceUsersDialogs,
      // ---- Whiteboard tab: Add lane, pinned to this client ----
      clientWhiteboardLaneDialog,
      // ---- Tickets tab: New ticket, preset to this client ----
      clientNewTicketDialog,
    ],
  },
];

export default clientDetailRoute;
